import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Calendar, BarChart3, LogOut, User, Shield, PenLine } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import axios from "axios";
import ChatTab from "../components/ChatTab";
import ScheduleTab from "../components/ScheduleTab";
import StatsTab from "../components/StatsTab";
import useWebSocket from "../hooks/useWebSocket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: "chat", label: "Чаты", icon: MessageSquare },
  { id: "schedule", label: "Расписание", icon: Calendar },
  { id: "stats", label: "Статистика", icon: BarChart3 }
];

export default function HomePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("chat");
  const [user, setUser] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "" });

  const { notifications, clearNotifications } = useWebSocket(user);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Ignore error
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  const handleNotification = useCallback((notification) => {
    if (notification.type === "new_message") {
      toast.info(`${notification.data.sender_name}: ${notification.data.content.substring(0, 50)}...`);
    } else if (notification.type === "schedule_added") {
      toast.info(notification.message);
    } else if (notification.type === "stats_received") {
      toast.info(notification.message);
    }
  }, []);

  useEffect(() => {
    if (notifications.length > 0) {
      notifications.forEach(handleNotification);
      clearNotifications();
    }
  }, [notifications, handleNotification, clearNotifications]);

  const handleEditProfile = () => {
    setEditForm({
      first_name: user.first_name,
      last_name: user.last_name
    });
    setShowProfile(false);
    setShowEditProfile(true);
  };

  const saveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API}/users/profile`, {
        first_name: editForm.first_name,
        last_name: editForm.last_name
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      const updatedUser = { ...user, ...response.data };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      toast.success("Профиль обновлен");
      setShowEditProfile(false);
    } catch (error) {
      toast.error("Ошибка обновления профиля");
    }
  };

  // Safe access to user properties
  const userInitials = user?.first_name && user?.last_name 
    ? `${user.first_name[0]}${user.last_name[0]}`
    : "U";

  return (
    <div className="min-h-screen bg-white pb-20" data-testid="home-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100/50 px-4 py-4 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-bold text-black">
            ZD Golf
          </h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowProfile(!showProfile)}
            className="rounded-full relative"
            data-testid="profile-btn"
          >
            <div className="w-8 h-8 rounded-full bg-golf-green/10 flex items-center justify-center text-golf-green font-bold text-xs">
              {userInitials}
            </div>
          </Button>
        </div>
      </header>

      {/* Profile Dropdown */}
      {showProfile && user && (
        <div className="absolute right-4 top-16 z-50 bg-white rounded-2xl shadow-lg border border-gray-100 p-4 w-64 animate-scale-in">
          <div className="mb-4">
            <p className="font-semibold text-black">{user.first_name} {user.last_name}</p>
            <p className="text-sm text-apple-text-secondary">{user.email}</p>
            <p className="text-xs text-apple-text-tertiary capitalize mt-1">
              {user.role === "coach" ? "Тренер" : 
               user.role === "athlete" ? "Спортсмен" :
               user.role === "parent" ? "Родитель" : "Любитель"}
              {user.is_admin && " • Админ"}
              {user.is_master && " • Master"}
            </p>
          </div>
          
          <Button
            onClick={handleEditProfile}
            variant="ghost"
            className="w-full justify-start mb-1 text-gray-700 hover:bg-gray-50"
          >
            <PenLine className="w-4 h-4 mr-2" />
            Изменить имя
          </Button>

          {user.is_master && (
            <Button
              onClick={() => navigate("/admin")}
              variant="ghost"
              className="w-full justify-start mb-2 text-golf-green hover:bg-golf-green/10"
            >
              <Shield className="w-4 h-4 mr-2" />
              Управление
            </Button>
          )}
          
          <div className="h-px bg-gray-100 my-2" />
          
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Выйти
          </Button>
        </div>
      )}

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Изменить имя</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Имя</Label>
              <Input 
                value={editForm.first_name}
                onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Фамилия</Label>
              <Input 
                value={editForm.last_name}
                onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
              />
            </div>
            <Button onClick={saveProfile} className="w-full bg-golf-green hover:bg-golf-green-hover">
              Сохранить
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tab Content */}
      <div className="px-4 py-4">
        {activeTab === "chat" && <ChatTab user={user} />}
        {activeTab === "schedule" && <ScheduleTab user={user} />}
        {activeTab === "stats" && <StatsTab user={user} />}
      </div>

      {/* Bottom Navigation */}
      <nav className="bottom-nav safe-area-inset-bottom" data-testid="bottom-nav">
        <div className="flex justify-around items-center py-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center py-2 px-4 transition-colors ${
                  isActive ? "text-golf-green" : "text-apple-text-tertiary"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className={`w-6 h-6 ${isActive ? "stroke-[2.5px]" : ""}`} />
                <span className={`text-xs mt-1 ${isActive ? "font-semibold" : "font-medium"}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Click outside to close profile */}
      {showProfile && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowProfile(false)}
        />
      )}
    </div>
  );
}
